import { useEffect, useRef, useState } from "react";

// Reveals a static string at the same cadence NutriCoach's real answers
// stream at. Mirrors the SSE token drain in `use-chat-session.ts`: text is
// released on requestAnimationFrame at a steady rate (no catch-up
// acceleration), so a "fake" stream is visually indistinguishable from a real
// one. Keep TARGET_CHARS_PER_SECOND in sync with the value there. Callers that
// want a different feel (e.g. slower interim status lines) can override it.
const TARGET_CHARS_PER_SECOND = 12;

interface UseTypewriterResult {
  /** The portion of `fullText` revealed so far. */
  text: string;
  /** True once the entire string has been revealed (or when disabled). */
  isDone: boolean;
}

/**
 * Progressively reveals `fullText`, restarting whenever it changes. When
 * `enabled` is false the full text is returned immediately with no animation
 * and no per-render state writes — cheap enough to call on every message
 * bubble and only animate the ones that opt in.
 */
export const useTypewriter = (
  fullText: string,
  enabled: boolean = true,
  charsPerSecond: number = TARGET_CHARS_PER_SECOND,
): UseTypewriterResult => {
  const [revealed, setRevealed] = useState("");
  const [isDone, setIsDone] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let pending = fullText;
    let shown = "";
    let lastTickAt: number | null = null;

    setRevealed("");
    setIsDone(false);

    const drain = (now: number) => {
      rafRef.current = null;
      if (lastTickAt === null) lastTickAt = now;
      const elapsed = now - lastTickAt;
      lastTickAt = now;

      const release = Math.max(1, Math.floor((elapsed * charsPerSecond) / 1000));
      const chunk = pending.slice(0, release);
      pending = pending.slice(release);
      if (chunk) {
        shown += chunk;
        setRevealed(shown);
      }

      if (pending.length > 0) {
        rafRef.current = requestAnimationFrame(drain);
      } else {
        lastTickAt = null;
        setIsDone(true);
      }
    };

    rafRef.current = requestAnimationFrame(drain);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [fullText, enabled, charsPerSecond]);

  if (!enabled) return { text: fullText, isDone: true };
  return { text: revealed, isDone };
};
